import { Link } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { Reveal } from "@/hooks/useReveal";
import { Play, ArrowUpRight } from "lucide-react";

const QUARTERS = [
  { n: "Q1", label: "The Warm-Up" },
  { n: "Q2", label: "The Playbook" },
  { n: "Q3", label: "The Full-Court Press" },
  { n: "Q4", label: "The Buzzer Beater" },
];

/**
 * Homepage banner promoting the /gameplan campaign page.
 */
const GamePlanPromo = () => {
  return (
    <section
      data-testid="gameplan-promo-section"
      className="relative bg-black text-white py-28 md:py-40 px-6 md:px-12 overflow-hidden"
    >
      {/* Subtle hardwood grain, echoes the /gameplan hero */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="promo-hardwood" patternUnits="userSpaceOnUse" width="80" height="120">
              <path d="M0,0 L0,120 M40,0 L40,120 M80,0 L80,120" stroke="#B8945A" strokeWidth="0.5" opacity="0.4" />
              <path d="M0,40 L80,40 M0,80 L80,80" stroke="#B8945A" strokeWidth="0.3" opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#promo-hardwood)" />
        </svg>
      </div>

      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Copy */}
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-[10px] tracking-[0.32em] uppercase mb-6 text-[#B8945A]">
              Campaign · Home-Court Advantage
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              Buying or selling
              <br />
              is a <em className="italic font-light text-[#B8945A]">four-quarter game.</em>
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-base md:text-lg text-white/65 mt-8 leading-relaxed max-w-xl">
              Coach Manzano built a video game plan that walks you through every play —
              from pregame prep to the final buzzer — so you always know your next move.
            </p>
          </Reveal>

          <Reveal delay={260}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              {QUARTERS.map((q) => (
                <div key={q.n} className="flex items-center gap-2">
                  <span className="text-[10px] tracking-[0.24em] uppercase text-[#B8945A]">{q.n}</span>
                  <span className="text-[10px] tracking-[0.24em] uppercase text-white/40">{q.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={340}>
            <Link
              to="/gameplan"
              data-testid="gameplan-promo-cta"
              className="group inline-flex items-center gap-3 mt-12 px-10 py-4 bg-[#B8945A] text-black border border-[#B8945A] text-[11px] uppercase tracking-[0.28em] hover:bg-transparent hover:text-[#B8945A] transition-all duration-500"
            >
              Watch the game plan
              <ArrowUpRight size={14} strokeWidth={2} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5">
          <Reveal delay={160}>
            <Link
              to="/gameplan"
              data-testid="gameplan-promo-thumbnail"
              className="group relative block w-full aspect-video overflow-hidden ring-1 ring-white/10"
            >
              <img
                src={resolveImage("/api/files/manzano-real-estate/gameplan/q1-poster.jpg")}
                alt="The Game Plan — Coach Manzano"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div className="w-16 h-16 rounded-full border border-[#B8945A] flex items-center justify-center backdrop-blur-sm bg-black/40 group-hover:scale-110 transition-transform duration-300">
                  <Play size={22} className="text-[#B8945A] translate-x-[1px]" fill="currentColor" />
                </div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8945A]">Tip off</p>
              </div>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default GamePlanPromo;
